/**
 * TrackingWidget — Phase 18.15
 *
 * Prominent tracking lookup on the homepage. Hits our own backend
 * (`POST /api/public/tracking`) which proxies the 5999Cargo Group
 * tracking source. Never calls the upstream proxy directly so the
 * frontend stays decoupled from credentials/cors.
 *
 * Four interaction states:
 *   - idle:    big input + "Track nu" button
 *   - loading: spinner + "Trackinggegevens worden opgehaald..."
 *   - success: status badge + progress + event timeline
 *   - error:   localised message + phone + email fallback CTAs
 */
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  Search,
  Loader2,
  Package,
  Phone,
  Mail,
  AlertCircle,
  CheckCircle2,
  Truck,
  Plane,
  Ship,
  Warehouse,
  ArrowRight,
} from "lucide-react";
import { useConfig } from "../context/ConfigContext";
import { publicDate } from "./publicTrackingDate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_ICON_MAP = {
  warehouse_received: Warehouse,
  in_warehouse: Warehouse,
  in_transit: Truck,
  out_for_delivery: Truck,
  departed: Ship,
  arrived: Plane,
  customs: Package,
  delivered: CheckCircle2,
  ready_for_pickup: CheckCircle2,
};

const ERROR_COPY = {
  invalid_tracking_number: {
    title: "Ongeldig trackingnummer",
    body:
      "Controleer of u het volledige nummer heeft ingevoerd. Bijvoorbeeld: W523518422761.",
  },
  tracking_not_found: {
    title: "Geen zending gevonden",
    body:
      "We konden geen zending vinden met dit trackingnummer. Controleer het nummer of neem contact op via telefoon of e-mail.",
  },
  tracking_upstream_timeout: {
    title: "Het duurt langer dan verwacht",
    body:
      "Tracking duurt op dit moment langer dan verwacht. Probeer het over enkele seconden opnieuw.",
  },
  tracking_upstream_unavailable: {
    title: "Tracking tijdelijk niet beschikbaar",
    body:
      "Onze tracking-koppeling is tijdelijk niet bereikbaar. Probeer het later opnieuw of neem contact op.",
  },
  rate_limited: {
    title: "Te veel verzoeken",
    body:
      "U heeft veel zoekopdrachten gedaan in korte tijd. Wacht even en probeer opnieuw.",
  },
  network: {
    title: "Verbinding mislukt",
    body:
      "Er ging iets mis tijdens het ophalen. Controleer uw internetverbinding en probeer opnieuw.",
  },
};

const PRESET_DESTINATIONS = ["Curaçao", "Aruba", "Bonaire", "Sint Maarten", "Suriname"];

export default function TrackingWidget() {
  const { phone, email } = useConfig();
  const [value, setValue] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | success | error
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTracking, setActiveTracking] = useState("");
  const [submission, setSubmission] = useState(0);
  const [refreshStatus, setRefreshStatus] = useState("live");
  const resultRef = useRef(null);
  const requestVersion = useRef(0);

  // Sprint Feb 2026 — auto-lookup vanaf ?tn={W-nummer} query (voor QR-scans
  // vanuit shipping labels).  Rijkt de input voor + submit direct.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tn = (
        params.get("tn") ||
        params.get("tracking") ||
        params.get("tracking_number") ||
        ""
      ).trim();
      if (tn) {
        setValue(tn);
        // Fire lookup once; we use a small delay to let React state settle.
        setTimeout(() => {
          const form = document.getElementById("tracking-widget-form");
          if (form) {
            form.requestSubmit
              ? form.requestSubmit()
              : form.dispatchEvent(
                  new Event("submit", { cancelable: true, bubbles: true })
                );
          }
        }, 50);
      }
    } catch {
      // Ignore — auto-lookup is best-effort.
    }
  }, []);

  // After successful track, smooth-scroll the result card into view on mobile
  useEffect(() => {
    if (state === "success" && resultRef.current) {
      try {
        resultRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
      } catch {
        // ignore — scrolling is a nice-to-have
      }
    }
  }, [state]);

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const trimmed = (value || "").trim();
    if (!trimmed) return;
    // Fence immediately at submit, before React's effect cleanup can run.
    requestVersion.current += 1;
    setState("loading");
    setData(null);
    setError(null);
    setActiveTracking(trimmed);
    setSubmission((n) => n + 1);
  };

  useEffect(() => {
    if (!activeTracking) return undefined;
    const version = requestVersion.current;
    let disposed = false, pending = false, hasResult = false, timer, controller;
    const refresh = async () => {
      clearTimeout(timer);
      if (disposed || version !== requestVersion.current || pending || document.hidden) return;
      if (!navigator.onLine) {
        setRefreshStatus("offline");
        if (!hasResult) { setError({ code: "network" }); setState("error"); }
        return;
      }
      pending = true;
      controller = new AbortController();
      let retryAfter = 5000, stop = false;
      try {
        const res = await axios.post(`${BACKEND_URL}/api/public/tracking`, { tracking_number: activeTracking }, {
          signal: controller.signal, timeout: 12000,
        });
        if (disposed || version !== requestVersion.current) return;
        hasResult = true;
        setData(res.data); setError(null); setState("success"); setRefreshStatus("live");
      } catch (err) {
        if (disposed || version !== requestVersion.current || axios.isCancel(err)) return;
        const payload = err?.response?.data?.detail || {};
        const code = payload.code || "network";
        retryAfter = Math.max(15000, (Number(err?.response?.headers?.["retry-after"]) || 0) * 1000);
        if (err?.response?.status === 429) retryAfter = Math.max(60000, retryAfter);
        stop = ["invalid_tracking_number", "tracking_not_found"].includes(code);
        setRefreshStatus("stale");
        if (!hasResult || stop) { setError({ code, message: payload.message }); setState("error"); }
      } finally {
        pending = false;
        if (!disposed && version === requestVersion.current && !stop && !document.hidden && navigator.onLine) timer = setTimeout(refresh, retryAfter);
      }
    };
    const resume = () => { if (!document.hidden && navigator.onLine) refresh(); };
    const suspend = () => {
      clearTimeout(timer);
      if (document.hidden || !navigator.onLine) controller?.abort();
      if (!navigator.onLine) setRefreshStatus("offline");
      else if (!document.hidden) resume();
    };
    refresh();
    document.addEventListener("visibilitychange", suspend);
    window.addEventListener("online", resume);
    window.addEventListener("offline", suspend);
    return () => {
      disposed = true; clearTimeout(timer); controller?.abort();
      document.removeEventListener("visibilitychange", suspend);
      window.removeEventListener("online", resume);
      window.removeEventListener("offline", suspend);
    };
  }, [activeTracking, submission]);

  return (
    <section
      id="tracking-widget"
      className="relative w-full"
      data-testid="tracking-widget"
    >
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#082c50] via-[#0a335c] to-[#1a5193] shadow-[0_24px_50px_-25px_rgba(8,44,80,0.6)] border border-white/10 px-6 py-10 sm:px-8 sm:py-12 md:px-12 md:py-14">
        <div className="relative grid grid-cols-1 md:grid-cols-[1.1fr,0.9fr] gap-10 md:gap-12 items-start">
          {/* LEFT — input panel */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] uppercase tracking-[0.18em] font-semibold mb-4">
              <Package className="w-3.5 h-3.5" />
              Realtime tracking
            </div>
            <h2
              className="font-bold text-3xl sm:text-4xl tracking-tight leading-tight"
              data-testid="tracking-widget-title"
            >
              Volg uw zending live.
            </h2>
            <p className="mt-3 text-white/75 text-base max-w-md leading-relaxed">
              Voer uw W-nummer of trackingnummer in om de status van uw cargo te
              zien — direct opgehaald uit ons logistieke systeem.
            </p>

            <form
              id="tracking-widget-form"
              onSubmit={handleSubmit}
              className="mt-8 flex flex-col sm:flex-row gap-4"
              data-testid="tracking-form"
            >
              <div className="relative flex-1">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#082c50]/60" />
                <input
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="Bijv. W523518422761"
                  className="w-full h-14 pl-14 pr-5 rounded-xl bg-white text-[#082c50] placeholder:text-[#082c50]/40 font-medium tracking-tight focus:outline-none focus:ring-4 focus:ring-cyan-400/30 transition-all"
                  data-testid="tracking-input"
                  aria-label="Trackingnummer"
                  autoComplete="off"
                  spellCheck="false"
                  maxLength={50}
                />
              </div>
              <button
                type="submit"
                disabled={state === "loading" || !value.trim()}
                className="h-14 px-7 rounded-xl bg-white text-[#082c50] hover:bg-cyan-50 font-bold tracking-tight text-base disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
                data-testid="tracking-submit"
              >
                {state === "loading" ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Bezig…</span>
                  </>
                ) : (
                  <>
                    <span>Track nu</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/60">
              <span>Voorbeeld: W523518422761</span>
              <span>Realtime data</span>
              <span>Geen account nodig</span>
            </div>
          </div>

          {/* RIGHT — result panel */}
          <div ref={resultRef}>
            {state === "idle" && (
              <div
                className="rounded-2xl border border-dashed border-white/20 bg-white/[0.04] backdrop-blur p-8 text-center text-white/70"
                data-testid="tracking-state-idle"
              >
                <Package className="w-10 h-10 mx-auto text-white/40" />
                <p className="mt-4 text-sm">
                  Uw resultaat verschijnt hier zodra u een trackingnummer invoert.
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-x-4 gap-y-2 text-[11px] uppercase tracking-[0.18em] text-white/45">
                  {PRESET_DESTINATIONS.map((d) => (
                    <span key={d}>{d}</span>
                  ))}
                </div>
              </div>
            )}

            {state === "loading" && (
              <div
                className="rounded-2xl border border-white/15 bg-white/[0.05] backdrop-blur p-8 animate-pulse"
                data-testid="tracking-state-loading"
              >
                <div className="flex items-center gap-3 text-white/85">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="font-semibold text-sm tracking-wide uppercase">
                    Trackinggegevens worden opgehaald…
                  </span>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="h-3 rounded bg-white/15 w-3/4" />
                  <div className="h-3 rounded bg-white/10 w-1/2" />
                  <div className="h-3 rounded bg-white/10 w-2/3" />
                </div>
              </div>
            )}

            {state === "success" && data && (
              <TrackingResultCard data={data} refreshStatus={refreshStatus} />
            )}

            {state === "error" && error && (
              <TrackingErrorCard
                error={error}
                phone={phone}
                emailAddress={email}
                onRetry={handleSubmit}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function TrackingResultCard({ data, refreshStatus }) {
  const history = (Array.isArray(data.events) ? data.events : [])
    .filter((event) => event?.processed === true && publicDate(event.date) && publicDate(event.date).getTime() <= Date.now())
    .sort((a, b) => publicDate(a.date) - publicDate(b.date));
  const currentDate = publicDate(data.last_update);
  const hasCurrent = data.status_processed === true && (!currentDate || currentDate.getTime() <= Date.now());
  const Icon = (hasCurrent && STATUS_ICON_MAP[data.status]) || Package;
  const pct = hasCurrent ? Math.max(0, Math.min(100, data.progress_percent || 0)) : 0;
  const fresh = refreshStatus === "offline" ? "Offline" : refreshStatus === "stale"
    ? "Laatst opgehaald" : data.data_freshness === "cached" ? "Recent opgehaald" : "Live";

  return (
    <div
      className="rounded-2xl bg-white shadow-xl border border-white/10 p-6 md:p-7"
      data-testid="tracking-state-success"
    >
      <div className="flex flex-wrap items-start gap-3 sm:gap-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#082c50] text-white flex items-center justify-center">
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#1a5193] font-bold break-all" data-testid="tracking-result-number">
            Trackingnummer · {data.tracking_number}
          </div>
          <div
            className="font-bold text-xl text-[#082c50] mt-1 leading-tight"
            data-testid="tracking-status-label"
          >
            {hasCurrent ? data.status_label : "Nog geen verwerkte trackingstatus"}
          </div>
          {hasCurrent && <div className="text-xs mt-1 text-slate-600" data-testid="tracking-status-basis">
            {data.update_source === "automatic" || data.tracking_basis === "automatic" ? "Automatisch bijgewerkt" : data.update_source === "manual" || data.tracking_basis === "manual" ? "Handmatig bijgewerkt" : data.tracking_basis === "confirmed" ? "Bevestigd" : data.tracking_basis === "planned" ? "Volgens planning" : "Geregistreerd"}
          </div>}
          {hasCurrent && currentDate && (
            <time className="block text-xs text-[#514e4e] mt-1" dateTime={data.last_update} data-testid="tracking-last-update">
              Status sinds · {currentDate.toLocaleString("nl-NL", { timeZone: "Europe/Amsterdam" })}
            </time>
          )}
          {hasCurrent && !currentDate && <span className="block text-xs text-[#514e4e] mt-1" data-testid="tracking-update-time-unknown">Bijwerktijd niet vastgelegd</span>}
        </div>
        <span
          className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1"
          title={fresh}
          data-testid="tracking-freshness"
          role="status"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          {fresh}
        </span>
      </div>

      {/* progress bar */}
      <div className="mt-5">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-[#1a5193] font-bold mb-1.5">
          <span>Voortgang</span>
          <span className="font-bold text-[#082c50]" data-testid="tracking-progress-value">{pct}%</span>
        </div>
        <div className="w-full h-2 rounded-full bg-[#082c50]/10 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#1a5193] to-cyan-400 transition-[width]"
            style={{ width: `${pct}%` }}
            data-testid="tracking-progress-bar"
          />
        </div>
      </div>

      {/* timeline */}
      {history.length > 0 && (
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#1a5193] font-bold mb-3" data-testid="tracking-history-heading">
            Historie
          </div>
          <ol
            className="relative border-l-2 border-[#082c50]/10 ml-1"
            data-testid="tracking-timeline"
          >
            {history.map((ev, idx) => (
              <li key={idx} className="relative pl-5 pb-4 last:pb-0 break-words" data-testid={`tracking-history-event-${idx}`}>
                <span
                  className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full ring-4 ${
                    idx === history.length - 1
                      ? "bg-[#1a5193] ring-[#1a5193]/15"
                      : "bg-[#082c50]/20 ring-[#082c50]/5"
                  }`}
                />
                <div className="text-sm font-semibold text-[#082c50]" data-testid={`tracking-history-label-${idx}`}>
                  {ev.status_label_nl}
                </div>
                {ev.date && (
                  <time className="block text-xs text-[#514e4e] mt-0.5" dateTime={ev.date} data-testid={`tracking-history-date-${idx}`}>
                    {publicDate(ev.date).toLocaleString("nl-NL", { timeZone: "Europe/Amsterdam" })}
                  </time>
                )}
                {["planned", "confirmed", "manual", "automatic"].includes(ev.tracking_basis) && (
                  <div className="text-xs text-slate-500 mt-0.5" data-testid={`tracking-history-basis-${idx}`}>
                    {ev.tracking_basis === "automatic" ? "Automatisch bijgewerkt" : ev.tracking_basis === "confirmed" ? "Bevestigd" : ev.tracking_basis === "planned" ? "Volgens planning" : "Handmatig bijgewerkt"}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function TrackingErrorCard({ error, phone, emailAddress, onRetry }) {
  const copy = ERROR_COPY[error.code] || ERROR_COPY.network;
  return (
    <div
      className="rounded-2xl bg-white shadow-xl border border-white/10 p-6 md:p-7"
      data-testid="tracking-state-error"
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#082c50]/10 text-[#082c50] flex items-center justify-center">
          <AlertCircle className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-lg text-[#082c50] leading-tight">
            {copy.title}
          </div>
          <p className="text-sm text-[#514e4e] mt-1 leading-relaxed">
            {error.message || copy.body}
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <a
          href={`tel:${phone || "+31488235999"}`}
          className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg bg-[#082c50] text-white hover:bg-[#1a5193] font-semibold text-sm transition-colors"
          data-testid="tracking-error-call"
        >
          <Phone className="w-4 h-4" />
          Bel ons
        </a>
        <a
          href={`mailto:${emailAddress || "info@5999cargo.nl"}`}
          className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg bg-white text-[#082c50] border border-[#082c50]/20 hover:border-[#082c50] font-semibold text-sm transition-colors"
          data-testid="tracking-error-email"
        >
          <Mail className="w-4 h-4" />
          Mail ons
        </a>
      </div>

      {error.code !== "invalid_tracking_number" && (
        <button
          onClick={onRetry}
          className="mt-3 w-full h-10 rounded-lg text-sm font-semibold text-[#1a5193] hover:bg-[#082c50]/5 transition-colors"
          data-testid="tracking-error-retry"
        >
          Opnieuw proberen
        </button>
      )}
    </div>
  );
}
